/**
 * Rules of the container and utility blocks that are plain block-state changes
 * (game/blockActions.ts calls these; the same code runs offline, on the server
 * and in the client's prediction): which state a placed block takes, what a
 * right-click does to a composter / cauldron / cake / flower pot / candle /
 * campfire, and how a double chest splits. DOM-free.
 *
 * What needs stored stacks (containers, hoppers, signs, frames) is the host's:
 * server/actions/utility.ts.
 */
import { I, ITEM_BASE } from '../itemIds';
import { isEmpty, stackOf, type Stack } from '../types';
import {
  CH_FACING, CH_KIND, CH_OPEN, CH_TYPE, POTTED, UK, UTIL, U_A, U_B, U_KIND,
  chestHalvesMatch, chestId, chestPartnerOffset, uid, utilityCanonical,
} from './defs';
import { comparatorSource } from '../../world/redstone';

const FLINT_AND_STEEL = ITEM_BASE + 8;

interface Env {
  readonly world: { getBlock(x: number, y: number, z: number): number };
  canEdit(x: number, y: number, z: number): boolean;
  setBlock(x: number, y: number, z: number, id: number): void;
  rnd(): number;
  dropBlock(stack: Stack, x: number, y: number, z: number): void;
  fx(kind: 'place' | 'bonemeal' | 'bucket' | 'ignite' | 'break', x: number, y: number, z: number, d: number): void;
  readonly predict?: boolean;
}
interface Actor {
  readonly x: number; readonly y: number; readonly z: number;
  readonly yaw: number;
  readonly creative: boolean;
  readonly held: Stack;
  consumeHeld(n: number): void;
  wearHeld(n: number): void;
  swapHeld(id: number): void;
  give(stack: Stack): void;
  /** Eat: false when the player is not hungry (nothing happens). */
  feed?(hunger: number, saturation: number): boolean;
}
interface Hit { x: number; y: number; z: number; nx: number; ny: number; nz: number; id: number }

const faceOf = (nx: number, ny: number, nz: number): number => (nx > 0 ? 0 : nx < 0 ? 1 : ny > 0 ? 2 : ny < 0 ? 3 : nz > 0 ? 4 : 5);
const LEFT: ReadonlyArray<readonly [number, number]> = [[-1, 0], [0, 1], [1, 0], [0, -1]];

/**
 * The state a utility block takes when placed in (tx, ty, tz): undefined when
 * `sel` is not one with a rule here, -1 when it cannot go there. `facing` is
 * the placer's horizontal gaze (0 +Z, 1 +X, 2 -Z, 3 -X), `eye` the vector from
 * the placer's eyes to the clicked point. A chest put beside a matching single
 * chest turns both into the halves of a double one.
 */
export function utilityPlaceId(
  env: Env, a: Actor, sel: number, hit: Hit, tx: number, ty: number, tz: number, facing: number,
  eye: readonly [number, number, number], sneaking = false,
): number | undefined {
  registerUtilityRedstone();
  switch (U_KIND[sel]) {
    case UK.CHEST: {
      const kind = CH_KIND[sel];
      if (!sneaking) {
        for (const type of [1, 2]) {
          const [lx, lz] = LEFT[facing];
          const dx = type === 1 ? lx : -lx, dz = type === 1 ? lz : -lz;
          const other = env.world.getBlock(tx + dx, ty, tz + dz);
          if (CH_KIND[other] !== kind || CH_TYPE[other] !== 0 || CH_FACING[other] !== facing || CH_OPEN[other] || !env.canEdit(tx + dx, ty, tz + dz)) continue;
          env.setBlock(tx + dx, ty, tz + dz, chestId(kind, type === 1 ? 2 : 1, facing, false));
          return chestId(kind, type, facing, false);
        }
      }
      return chestId(kind, 0, facing, false);
    }
    case UK.ENDER: return uid(UK.ENDER, facing);
    case UK.HOPPER: {
      // the spout points at the block that was clicked; never up
      const to = faceOf(-hit.nx, -hit.ny, -hit.nz);
      return uid(UK.HOPPER, to === 2 ? 3 : to);
    }
    case UK.DISPENSER: case UK.DROPPER: {
      // the mouth faces the placer
      const ax = Math.abs(eye[0]), ay = Math.abs(eye[1]), az = Math.abs(eye[2]);
      const face = ay > ax && ay > az ? (eye[1] > 0 ? 3 : 2) : ax > az ? (eye[0] > 0 ? 1 : 0) : (eye[2] > 0 ? 5 : 4);
      return uid(U_KIND[sel], face);
    }
    case UK.SIGN: {
      if (hit.ny < 0) return -1;
      if (hit.ny === 0) return undefined; // a wall sign: blocks.ts picked it from the clicked face
      const r = ((Math.round(a.yaw / (Math.PI / 8)) % 16) + 16) % 16;
      return uid(UK.SIGN, U_A[sel], r);
    }
    case UK.CHAIN: return uid(UK.CHAIN, hit.nx !== 0 ? 0 : hit.ny !== 0 ? 1 : 2);
    default: return undefined;
  }
}

/** A chest half is going: its other half becomes a single chest again. Call before the block is removed. */
export function splitChest(env: Pick<Env, 'world' | 'setBlock'>, x: number, y: number, z: number, id: number): void {
  const off = chestPartnerOffset(id);
  if (!off) return;
  const ox = x + off[0], oz = z + off[1];
  const other = env.world.getBlock(ox, y, oz);
  if (!chestHalvesMatch(id, other)) return;
  env.setBlock(ox, y, oz, chestId(CH_KIND[other], 0, CH_FACING[other], CH_OPEN[other] === 1));
}

// ---- composter ----------------------------------------------------------------

/** ComposterBlock.COMPOSTABLES by item / block name: the chance one item adds a layer. */
const COMPOST_RULES: Array<[RegExp, number]> = [
  [/^(Cake|Pumpkin Pie)$/, 1],
  [/^(Bread|Baked Potato|Cookie|Hay Bale|.*Mushroom Block|Nether Wart Block|Warped Wart Block)$/, 0.85],
  [/^(Apple|Beetroot|Carrot|Cocoa Beans|Potato|Poisonous Potato|Wheat|Melon|Pumpkin|Jack o'Lantern|Sea Pickle|Lily Pad|Nether Wart|.*Fungus|.*Roots|.*Mushroom|.*Tulip|Poppy|Dandelion|Blue Orchid|Allium|Azure Bluet|Oxeye Daisy|Cornflower|Lily of the Valley|Fern|Spore Blossom|Azalea|Moss Block)$/, 0.65],
  [/^(Melon Slice|Sugar Cane|Cactus|Vine|Tall Grass|Large Fern|Sunflower|Lilac|Rose Bush|Peony|Nether Sprouts|.*Vines|Dried Kelp Block)$/, 0.5],
  [/(Leaves|Sapling|Seeds)$|^(Short Grass|Grass|Kelp|Dried Kelp|Seagrass|Sweet Berries|Glow Berries|Moss Carpet|Pink Petals|Hanging Roots)$/, 0.3],
];
const compostCache = new Map<number, number>();
/** Chance (0..1) that one of this item adds a compost layer; 0 when it cannot be composted. `nameOf` resolves item and block names. */
export function compostChance(id: number, nameOf: (id: number) => string): number {
  let c = compostCache.get(id);
  if (c === undefined) {
    const name = nameOf(id);
    c = COMPOST_RULES.find(([re]) => re.test(name))?.[1] ?? 0;
    compostCache.set(id, c);
  }
  return c;
}

/** Comparator output of the id-state utility blocks (0 for the others). */
export function utilitySignal(id: number): number {
  switch (U_KIND[id]) {
    case UK.COMPOSTER: return U_A[id];
    case UK.CAULDRON: return U_A[id] === 0 ? 0 : U_B[id];
    case UK.CAKE: return (7 - U_A[id]) * 2;
    default: return 0;
  }
}

let redstoneDone = false;
/** Tell the redstone sim what a comparator reads off a composter, cauldron or cake. Idempotent. */
export function registerUtilityRedstone(): void {
  if (redstoneDone) return;
  redstoneDone = true;
  const ids: number[] = [];
  for (let id = 0; id < U_KIND.length; id++) if (U_KIND[id] === UK.COMPOSTER || U_KIND[id] === UK.CAULDRON || U_KIND[id] === UK.CAKE) ids.push(id);
  comparatorSource(ids, (world, x, y, z) => utilitySignal(world.getBlock(x, y, z)));
}

/**
 * Add one item to a composter (a hopper or a hand). Returns the new block id,
 * or -1 when it takes nothing (full, ready, or not compostable).
 */
export function compostInto(id: number, chance: number, rnd: () => number): number {
  const level = U_A[id];
  if (U_KIND[id] !== UK.COMPOSTER || chance <= 0 || level >= 7) return -1;
  // the first layer always takes (vanilla)
  if (level > 0 && rnd() >= chance) return id;
  // vanilla waits a second at 7 before turning ready; here the seventh layer makes it ready at once
  return uid(UK.COMPOSTER, level + 1 >= 7 ? 8 : level + 1);
}

// ---- right-click ----------------------------------------------------------------

/** Would right-click act on this utility block? */
export function utilityInteractable(id: number): boolean {
  const k = U_KIND[id];
  return k === UK.CHEST || k === UK.ENDER || k === UK.HOPPER || k === UK.DISPENSER || k === UK.DROPPER || k === UK.SHULKER
    || k === UK.SIGN || k === UK.WALL_SIGN || k === UK.COMPOSTER || k === UK.CAULDRON || k === UK.CAKE || k === UK.FRAME || k === UK.POT;
}

/**
 * Right-click on a utility block whose reaction is a state change. Returns
 * true when the click was used up.
 */
export function useUtilityBlock(env: Env, a: Actor, hit: Hit, nameOf: (id: number) => string): boolean {
  const { x, y, z, id } = hit;
  const kind = U_KIND[id];
  if (kind === UK.NONE || !env.canEdit(x, y, z)) return false;
  const held = a.held;
  const heldId = isEmpty(held) ? 0 : held.id;
  const live = !env.predict;
  switch (kind) {
    case UK.COMPOSTER: {
      const level = U_A[id];
      if (level === 8) {
        if (live) {
          env.setBlock(x, y, z, uid(UK.COMPOSTER, 0));
          env.dropBlock(stackOf(I.BONE_MEAL), x, y + 1, z);
          env.fx('bonemeal', x, y, z, 0);
        }
        return true;
      }
      const chance = compostChance(heldId, nameOf);
      if (chance <= 0 || level >= 7) return false;
      if (live) {
        const next = compostInto(id, chance, env.rnd);
        if (next !== id) env.setBlock(x, y, z, next);
        env.fx('bonemeal', x, y, z, 0);
        if (!a.creative) a.consumeHeld(1);
      }
      return true;
    }
    case UK.CAULDRON: {
      const content = U_A[id], level = U_B[id];
      const set = (c: number, l: number): void => { env.setBlock(x, y, z, uid(UK.CAULDRON, c, l)); };
      if (heldId === I.WATER_BUCKET || heldId === I.LAVA_BUCKET) {
        const water = heldId === I.WATER_BUCKET;
        if (content === (water ? 1 : 2) && level === 3) return false;
        set(water ? 1 : 2, 3);
        env.fx('bucket', x, y, z, water ? 0 : 2);
        if (live && !a.creative) a.swapHeld(I.BUCKET);
        return true;
      }
      if (heldId === I.BUCKET) {
        if (content === 0 || level < 3) return false;
        set(0, 0);
        env.fx('bucket', x, y, z, content === 1 ? 1 : 3);
        if (live && !a.creative) a.swapHeld(content === 1 ? I.WATER_BUCKET : I.LAVA_BUCKET);
        return true;
      }
      if (heldId === I.GLASS_BOTTLE) {
        if (content !== 1) return false;
        if (level > 1) set(1, level - 1); else set(0, 0);
        env.fx('bucket', x, y, z, 1);
        if (live && !a.creative) { a.consumeHeld(1); a.give(stackOf(I.POTION)); }
        return true;
      }
      if (heldId === I.POTION && !held.dmg && !held.ench && !held.fw) {
        // a water bottle tops the cauldron up by a third
        if (content === 2 || (content === 1 && level >= 3)) return false;
        set(1, content === 1 ? level + 1 : 1);
        env.fx('bucket', x, y, z, 0);
        if (live && !a.creative) { a.consumeHeld(1); a.give(stackOf(I.GLASS_BOTTLE)); }
        return true;
      }
      return false;
    }
    case UK.CAKE: {
      if (env.predict) return true;
      // FoodProperties: 2 hunger, 0.1 saturation modifier
      if (a.feed && !a.feed(2, 0.4) && !a.creative) return false;
      const bites = U_A[id] + 1;
      env.setBlock(x, y, z, bites >= 7 ? 0 : uid(UK.CAKE, bites));
      return true;
    }
    case UK.POT: {
      const plant = U_A[id];
      if (plant === 0) {
        const potted = POTTED.find(([, p]) => p === heldId);
        if (!potted) return false;
        env.setBlock(x, y, z, potted[0]);
        if (live && !a.creative) a.consumeHeld(1);
        return true;
      }
      env.setBlock(x, y, z, UTIL.POT);
      if (live && !a.creative) a.give(stackOf(plant));
      return true;
    }
    case UK.CANDLE: {
      const n = U_A[id], lit = U_B[id];
      if (heldId !== 0 && utilityCanonical(heldId) === UTIL.CANDLE && n < 4) {
        env.setBlock(x, y, z, uid(UK.CANDLE, n + 1, lit));
        env.fx('place', x, y, z, id);
        if (live && !a.creative) a.consumeHeld(1);
        return true;
      }
      if ((heldId === FLINT_AND_STEEL || heldId === I.FIRE_CHARGE) && !lit) {
        env.setBlock(x, y, z, uid(UK.CANDLE, n, 1));
        env.fx('ignite', x, y, z, 0);
        if (live && !a.creative) { if (heldId === FLINT_AND_STEEL) a.wearHeld(1); else a.consumeHeld(1); }
        return true;
      }
      if (heldId === 0 && lit) { env.setBlock(x, y, z, uid(UK.CANDLE, n, 0)); return true; }
      return false;
    }
    case UK.CAMPFIRE: {
      const lit = U_A[id];
      if ((heldId === FLINT_AND_STEEL || heldId === I.FIRE_CHARGE) && !lit) {
        env.setBlock(x, y, z, uid(UK.CAMPFIRE, 1));
        env.fx('ignite', x, y, z, 0);
        if (live && !a.creative) { if (heldId === FLINT_AND_STEEL) a.wearHeld(1); else a.consumeHeld(1); }
        return true;
      }
      // a shovel puts it out
      if (lit && heldId >= ITEM_BASE && /Shovel$/.test(nameOf(heldId))) {
        env.setBlock(x, y, z, uid(UK.CAMPFIRE, 0));
        if (live && !a.creative) a.wearHeld(1);
        return true;
      }
      return false;
    }
    default: return false;
  }
}
